import { HTML_WIDGET_PREFIX, HtmlPage } from "./types";

export const parseHtmlPageFromMetadata = (key: string, value: string): HtmlPage | null => {
  if (!key.startsWith(HTML_WIDGET_PREFIX)) {
    return null;
  }

  try {
    const data = JSON.parse(value);
    const id = key.replace(HTML_WIDGET_PREFIX, "");
    
    return {
      id,
      name: data.name || "",
      description: data.description || "",
      slug: data.slug || "",
      html: data.html || "",
      javascript: data.javascript || "",
      css: data.css || "",
      type: data.type || "",
      isActive: data.isActive !== false,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
    };
  } catch (error) {
    console.error("Failed to parse HTML page metadata:", error);

    return null;
  }
};

export const serializeHtmlPageToMetadata = (page: HtmlPage): { key: string; value: string } => {
  const key = HTML_WIDGET_PREFIX + page.id;
  const valueObj = {
    name: page.name,
    description: page.description,
    slug: page.slug,
    html: page.html,
    javascript: page.javascript,
    css: page.css,
    type: page.type,
    isActive: page.isActive,
    createdAt: page.createdAt,
    updatedAt: new Date().toISOString(),
  };
  
  const value = JSON.stringify(valueObj);

  return { key, value };
};

export const generatePageId = (): string => {
  const id = Date.now().toString() + "_" + Math.random().toString(36).substring(2, 11);

  return id;
};

export const filterHtmlPages = (pages: HtmlPage[], filters?: any): HtmlPage[] => {
  if (!filters) return pages;

  return pages.filter(page => {
    if (filters.type && page.type !== filters.type) {
      return false;
    }
    
    if (filters.isActive !== undefined && page.isActive !== filters.isActive) {
      return false;
    }
    
    if (filters.search) {
      const search = filters.search.toLowerCase();

      return (
        page.name.toLowerCase().includes(search) ||
        (page.description && page.description.toLowerCase().includes(search))
      );
    }
    
    return true;
  });
};

export const createDefaultPage = (): Partial<HtmlPage> => ({
  name: "",
  description: "",
  slug: "",
  html: "",
  javascript: "",
  css: "",
  type: "",
  isActive: true,
});

export const generateSlugFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

export const convertPageToPageContent = (page: HtmlPage): string => {
  // Extract content from HTML if it's a full document
  let extractedContent = page.html || '';
  
  // If HTML contains full document structure, extract body content
  if (extractedContent.includes('<!DOCTYPE html>') || extractedContent.includes('<html>')) {
    // Parse the HTML to extract body content
    const bodyMatch = extractedContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);

    if (bodyMatch) {
      extractedContent = bodyMatch[1];
    }
    
    // Also extract any styles from head if not already in CSS field
    if (!page.css || page.css.trim() === '') {
      const styleMatches = page.html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);

      if (styleMatches) {
        const extractedStyles = styleMatches.map(match => {
          const styleContent = match.match(/<style[^>]*>([\s\S]*?)<\/style>/i);

          return styleContent ? styleContent[1] : '';
        }).join('\n');
        
        // Update page CSS with extracted styles
        page.css = (page.css || '') + '\n' + extractedStyles;
      }
    }
  }

  // Use default type if empty
  const pageType = page.type?.trim() || 'page';

  // Create a clean content structure without full document tags
  return `
<div class="html-page" data-page-type="${pageType}" data-page-id="${page.id}">
  ${extractedContent}
</div>

${page.css ? `<style>
${page.css}
</style>` : ''}

${page.javascript ? `<script>
(function() {
${page.javascript}
})();
</script>` : ''}
  `.trim();
};

export const createPageMetadataFromPage = (page: HtmlPage) => {
  const pageType = page.type?.trim() || 'page';
  
  const metadata = [
    { key: 'pageType', value: pageType },
    { key: 'pageId', value: page.id },
    { key: 'isHtmlPage', value: 'true' },
  ];

  return metadata;
};